/**
 * /review/rules — Rule Review Console (PRP: rule-review-console)
 *
 * Visible only to users with the `reviewer` role. Default landing screen
 * for reviewers after login.
 *
 * Layout:
 *   Left panel  — category list with progress bars
 *   Right panel — cards for the selected category; discipline-match rules first
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';
import {
  BookOpen,
  Check,
  ChevronRight,
  CircleDot,
  MessageSquareWarning,
  ShieldAlert,
  ToggleLeft,
  X,
} from 'lucide-react';

import useAuthStore from '../stores/authStore';
import { RuleReviewAPI } from '../lib/ruleReviewApi';
import { cn, getErrorMessage } from '../lib/utils';
import {
  DECISION_COLORS,
  DECISION_LABELS,
  type CategoryProgress,
  type ReviewDecision,
  type RuleForReview,
} from '../types/ruleReview';

const REVIEWER_ROLES = ['reviewer', 'admin', 'super_admin'];

// ---------------------------------------------------------------------------
// Decision button config
// ---------------------------------------------------------------------------

const DECISION_OPTIONS: Array<{
  value: ReviewDecision;
  label: string;
  icon: React.ReactNode;
  className: string;
}> = [
  {
    value: 'agree',
    label: 'Agree',
    icon: <Check className="h-4 w-4" />,
    className:
      'border-green-300 dark:border-green-500/30 bg-green-50 dark:bg-green-500/15 text-green-800 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-500/20',
  },
  {
    value: 'agree_optional',
    label: 'Agree (optional)',
    icon: <ToggleLeft className="h-4 w-4" />,
    className: 'border-blue-300 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20',
  },
  {
    value: 'agree_carrier_overridable',
    label: 'Agree (carrier)',
    icon: <CircleDot className="h-4 w-4" />,
    className: 'border-sky-300 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/15 text-sky-800 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-500/20',
  },
  {
    value: 'contest',
    label: 'Contest',
    icon: <X className="h-4 w-4" />,
    className: 'border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 text-red-800 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20',
  },
  {
    value: 'needs_discussion',
    label: 'Needs discussion',
    icon: <MessageSquareWarning className="h-4 w-4" />,
    className:
      'border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20',
  },
];

// ---------------------------------------------------------------------------
// CategorySidebar
// ---------------------------------------------------------------------------

interface CategorySidebarProps {
  categories: CategoryProgress[];
  selected: string | null;
  onSelect: (cat: string) => void;
}

const CategorySidebar: React.FC<CategorySidebarProps> = ({
  categories,
  selected,
  onSelect,
}) => (
  <aside className="w-64 shrink-0 rounded-lg border bg-card shadow-sm">
    <div className="border-b px-4 py-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-muted-foreground">
        Categories
      </h2>
    </div>
    <ul className="divide-y text-sm">
      {categories.map((cat) => {
        const isActive = cat.category === selected;
        const complete = cat.reviewed_count >= cat.total_rules && cat.total_rules > 0;
        return (
          <li key={cat.category}>
            <button
              type="button"
              onClick={() => onSelect(cat.category)}
              className={cn(
                'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-muted',
                isActive && 'bg-primary/5',
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'font-medium capitalize',
                    isActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  {cat.category}
                </span>
                <div className="flex items-center gap-1">
                  {complete && (
                    <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  )}
                  <ChevronRight
                    className={cn(
                      'h-3.5 w-3.5 transition-colors',
                      isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500',
                    )}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      complete ? 'bg-green-500' : 'bg-primary',
                    )}
                    style={{ width: `${cat.pct_complete}%` }}
                  />
                </div>
                <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                  {cat.reviewed_count}/{cat.total_rules}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  </aside>
);

// ---------------------------------------------------------------------------
// ReviewCard
// ---------------------------------------------------------------------------

interface ReviewCardProps {
  rule: RuleForReview;
  onDecision: (
    ruleId: string,
    decision: ReviewDecision,
    comment: string,
    contestReason: string,
  ) => Promise<void>;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ rule, onDecision }) => {
  const [selectedDecision, setSelectedDecision] =
    React.useState<ReviewDecision | null>(
      (rule.my_decision as ReviewDecision) ?? null,
    );
  const [comment, setComment] = React.useState(rule.my_comment ?? '');
  const [contestReason, setContestReason] = React.useState(
    rule.my_contest_reason ?? '',
  );
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(!!rule.my_decision);
  const [error, setError] = React.useState<string | null>(null);

  const handleDecisionClick = (decision: ReviewDecision) => {
    setSelectedDecision(decision);
    setSaved(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedDecision) return;
    if (selectedDecision === 'contest' && !contestReason.trim()) {
      setError('Please provide a reason for contesting this rule.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onDecision(rule.id, selectedDecision, comment, contestReason);
      setSaved(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const displayText = rule.plain_english_render || rule.description;

  return (
    <article
      className={cn(
        'rounded-lg border bg-card shadow-sm transition-all',
        saved && 'border-green-200 dark:border-green-500/30 bg-green-50/30 dark:bg-green-500/10',
        rule.is_discipline_match && !saved && 'border-primary/30',
      )}
    >
      <div className="px-5 py-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 className="text-sm font-semibold text-foreground">
                {rule.name}
              </h3>
              {rule.is_discipline_match && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                  Your discipline
                </span>
              )}
              {saved && selectedDecision && (
                <span
                  className={cn(
                    'rounded border px-2 py-0.5 text-[10px] font-medium uppercase',
                    DECISION_COLORS[selectedDecision],
                  )}
                >
                  {DECISION_LABELS[selectedDecision]}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-muted-foreground">{displayText}</p>
          </div>
        </div>

        {/* Citations */}
        {rule.citations.length > 0 && (
          <ul className="mt-3 space-y-1">
            {rule.citations.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-muted-foreground">
                <BookOpen className="mt-0.5 h-3 w-3 shrink-0" />
                <span>
                  {c.source_type && (
                    <span className="font-medium">
                      {c.source_type.toUpperCase()}{' '}
                    </span>
                  )}
                  {c.reference}
                  {c.summary && ` — ${c.summary}`}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Tags / domains */}
        {(rule.tags.length > 0 || rule.domains.length > 0) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {rule.domains.map((d) => (
              <span
                key={d}
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:text-muted-foreground"
              >
                {d}
              </span>
            ))}
            {rule.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border bg-card px-2 py-0.5 text-[10px] text-slate-500 dark:text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Decision area */}
      <div className="border-t bg-slate-50/60 dark:bg-muted px-5 py-3">
        <div className="flex flex-wrap gap-1.5">
          {DECISION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleDecisionClick(opt.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-medium transition-all',
                opt.className,
                selectedDecision === opt.value
                  ? 'ring-2 ring-offset-1 ring-current'
                  : 'opacity-80 hover:opacity-100',
              )}
            >
              {opt.icon}
              {opt.label}
            </button>
          ))}
        </div>

        {/* Contest reason (required when contesting) */}
        {selectedDecision === 'contest' && (
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Reason for contesting <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              rows={2}
              value={contestReason}
              onChange={(e) => setContestReason(e.target.value)}
              placeholder="Cite the standard, code, or experience that contradicts this rule…"
              className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        )}

        {/* Optional comment */}
        <div className="mt-2">
          <textarea
            rows={1}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment…"
            className="w-full rounded border border-input bg-card px-2 py-1.5 text-xs text-slate-600 dark:text-muted-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>

        {error && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={!selectedDecision || saving}
            onClick={handleSubmit}
            className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : saved ? 'Update' : 'Save decision'}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
              <Check className="h-3 w-3" />
              Saved
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

// ---------------------------------------------------------------------------
// RuleReviewPage
// ---------------------------------------------------------------------------

export default function RuleReviewPage(): React.ReactElement {
  const { roles } = useAuthStore();
  const hasAccess = REVIEWER_ROLES.some((r) => roles.includes(r));

  const [categories, setCategories] = React.useState<CategoryProgress[] | null>(
    null,
  );
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null,
  );
  const [rules, setRules] = React.useState<RuleForReview[] | null>(null);
  const [catError, setCatError] = React.useState<string | null>(null);
  const [rulesError, setRulesError] = React.useState<string | null>(null);
  const [catLoading, setCatLoading] = React.useState(false);
  const [rulesLoading, setRulesLoading] = React.useState(false);

  // Local decision cache so card state survives category reload
  const [decisions, setDecisions] = React.useState<
    Record<string, { decision: ReviewDecision; comment: string; contestReason: string }>
  >({});

  // Load categories on mount
  React.useEffect(() => {
    if (!hasAccess) return;
    setCatLoading(true);
    RuleReviewAPI.listCategories()
      .then((cats) => {
        setCategories(cats);
        // Auto-select first incomplete category or first category
        const first =
          cats.find((c) => c.reviewed_count < c.total_rules) ?? cats[0];
        if (first) setSelectedCategory(first.category);
      })
      .catch((err) => setCatError(getErrorMessage(err)))
      .finally(() => setCatLoading(false));
  }, [hasAccess]);

  // Load rules whenever category changes
  React.useEffect(() => {
    if (!selectedCategory) return;
    setRulesLoading(true);
    setRulesError(null);
    RuleReviewAPI.listRules({ category: selectedCategory, limit: 200 })
      .then((list) => setRules(list))
      .catch((err) => setRulesError(getErrorMessage(err)))
      .finally(() => setRulesLoading(false));
  }, [selectedCategory]);

  const handleDecision = async (
    ruleId: string,
    decision: ReviewDecision,
    comment: string,
    contestReason: string,
  ) => {
    await RuleReviewAPI.submitReview(ruleId, {
      decision,
      comment: comment || undefined,
      contest_reason: contestReason || undefined,
    });

    // Update local decision cache
    setDecisions((prev) => ({
      ...prev,
      [ruleId]: { decision, comment, contestReason },
    }));

    // Update category progress optimistically
    setCategories((prev) => {
      if (!prev || !selectedCategory) return prev;
      return prev.map((c) => {
        if (c.category !== selectedCategory) return c;
        const wasReviewed = rules?.find((r) => r.id === ruleId)?.my_decision;
        const newReviewed = wasReviewed
          ? c.reviewed_count
          : c.reviewed_count + 1;
        return {
          ...c,
          reviewed_count: newReviewed,
          pct_complete:
            c.total_rules > 0
              ? Math.round((newReviewed / c.total_rules) * 1000) / 10
              : 0,
        };
      });
    });

    // Patch the rule in the list so the card knows it's been decided
    setRules((prev) =>
      prev
        ? prev.map((r) =>
            r.id === ruleId
              ? { ...r, my_decision: decision, my_comment: comment, my_contest_reason: contestReason }
              : r,
          )
        : prev,
    );
  };

  if (!hasAccess) {
    return (
      <div className="mx-auto max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
        <h1 className="mt-3 text-lg font-semibold">Reviewer access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The Rule Review console is only accessible to invited reviewers.
          Contact an admin if you believe you should have access.
        </p>
        <Navigate to="/" replace />
      </div>
    );
  }

  // Progress headline
  const totalRules = categories?.reduce((s, c) => s + c.total_rules, 0) ?? 0;
  const totalReviewed =
    categories?.reduce((s, c) => s + c.reviewed_count, 0) ?? 0;

  return (
    <div className="space-y-4">
      {/* Page header */}
      <header>
        <h1 className="text-2xl font-bold">Rule Review</h1>
        <p className="text-sm text-muted-foreground">
          {categories
            ? `${totalReviewed} of ${totalRules} rules reviewed`
            : 'Loading progress…'}
        </p>
      </header>

      {catError && (
        <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
          {catError}
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        {/* Category sidebar */}
        {catLoading ? (
          <aside className="w-64 shrink-0 rounded-lg border bg-card shadow-sm">
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          </aside>
        ) : categories && categories.length > 0 ? (
          <CategorySidebar
            categories={categories}
            selected={selectedCategory}
            onSelect={(cat) => {
              setSelectedCategory(cat);
              setRules(null);
            }}
          />
        ) : null}

        {/* Rule cards */}
        <div className="min-w-0 flex-1 space-y-3">
          {selectedCategory && (
            <div className="flex items-baseline justify-between">
              <h2 className="text-base font-semibold capitalize text-muted-foreground">
                {selectedCategory}
              </h2>
              {rules && (
                <span className="text-xs text-muted-foreground">
                  {rules.filter((r) => r.my_decision).length} / {rules.length} reviewed
                </span>
              )}
            </div>
          )}

          {rulesError && (
            <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
              {rulesError}
            </div>
          )}

          {rulesLoading && (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-40 animate-pulse rounded-lg border bg-card shadow-sm"
                />
              ))}
            </div>
          )}

          {!rulesLoading && rules && rules.length === 0 && (
            <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
              No rules in this category.
            </div>
          )}

          {!rulesLoading &&
            rules &&
            rules.map((rule) => (
              <ReviewCard
                key={rule.id}
                rule={rule}
                onDecision={handleDecision}
              />
            ))}

          {!selectedCategory && !catLoading && (
            <div className="flex h-40 items-center justify-center rounded-lg border border-dashed bg-card text-sm text-muted-foreground">
              Select a category from the left to start reviewing.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
